/**
 * Onglet "Insightlab" du module Séances unifié.
 *
 * Liste paginee filtrable des procedes (briques unitaires correspondant
 * a un seul des 5 procedes : Echauffement / Jeu / Situation / Exercice /
 * Match). Visibilite : procedes perso + club + templates systeme.
 *
 * Etat URL : ?tab=insightlab&labProcede=...&labSearch=...&labPage=...&labView=...
 * Les routes detail/edition/creation pointent pour l'instant vers
 * /dashboard/seances/insightlab/* (legacy) ; elles seront renommees en
 * /dashboard/seances/insightlab/* au C4 lors du rename ProcedeCard.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Search, ChevronLeft, ChevronRight, BookOpen, Sparkles } from 'lucide-react'
import { T } from '../../theme'
import { useAuth } from '../../context/AuthContext'
import ProcedeCard from '../ProcedeCard'
import {
  useProcedeList, useDeleteProcede, useDuplicateProcede,
} from '../../hooks/useProcedes'
import { ErrorState, SkeletonGrid, EmptyState, btnPrimary } from './_shared'

const PROCEDES = [
  { value: '',             label: 'Tous' },
  { value: 'echauffement', label: 'Échauffement' },
  { value: 'jeu',          label: 'Jeu' },
  { value: 'situation',    label: 'Situation' },
  { value: 'exercice',     label: 'Exercice' },
  { value: 'match',        label: 'Match' },
]

const THEMES = [
  { value: '',               label: 'Tous thèmes' },
  { value: 'pressing',       label: 'Pressing' },
  { value: 'construction',   label: 'Construction' },
  { value: 'transition_off', label: 'Transition off' },
  { value: 'transition_def', label: 'Transition déf' },
  { value: 'entre_lignes',   label: 'Entre les lignes' },
  { value: 'cote_fort',      label: 'Côté fort' },
  { value: 'finition',       label: 'Finition' },
  { value: 'bloc',           label: 'Bloc' },
  { value: 'cpa_off',        label: 'CPA' },
  { value: 'physique',       label: 'Physique' },
]

const PAGE_SIZE = 21


export default function InsightlabTab() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()

  const procede = params.get('labProcede') || ''
  const theme = params.get('labTheme') || ''
  const search = params.get('labSearch') || ''
  const view = params.get('labView') === 'templates' ? 'templates' : 'all'
  const page = Math.max(1, parseInt(params.get('labPage') || '1', 10) || 1)

  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => { setSearchInput(search) }, [search])
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        const next = new URLSearchParams(params)
        if (searchInput) next.set('labSearch', searchInput); else next.delete('labSearch')
        next.set('labPage', '1')
        setParams(next, { replace: true })
      }
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  const setUrl = (next) => setParams(next, { replace: false })

  const filters = useMemo(() => ({
    mode: view === 'templates' ? 'templates' : 'all',
    procede: procede || undefined,
    theme: theme || undefined,
    search: search || undefined,
    page, size: PAGE_SIZE,
  }), [view, procede, theme, search, page])

  const { data, loading, error, refetch } = useProcedeList(filters)
  const { mutate: doDuplicate } = useDuplicateProcede()
  const { mutate: doDelete } = useDeleteProcede()

  const items = data?.items || []
  const total = data?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const setProcede = (p) => {
    const next = new URLSearchParams(params)
    if (p) next.set('labProcede', p); else next.delete('labProcede')
    next.set('labPage', '1')
    setUrl(next)
  }
  const setTheme = (t) => {
    const next = new URLSearchParams(params)
    if (t) next.set('labTheme', t); else next.delete('labTheme')
    next.set('labPage', '1')
    setUrl(next)
  }
  const setView = (v) => {
    const next = new URLSearchParams(params)
    if (v === 'templates') next.set('labView', 'templates'); else next.delete('labView')
    next.set('labPage', '1')
    setUrl(next)
  }
  const setPage = (p) => {
    const next = new URLSearchParams(params)
    next.set('labPage', String(p))
    setUrl(next)
  }

  const handleDuplicate = async (exo) => {
    try {
      const copy = await doDuplicate(exo.id)
      // Routes legacy ; renommees au C4
      navigate(`/dashboard/seances/insightlab/${copy.id}/edit`)
    } catch {
      alert('Échec de la duplication. Réessaie.')
    }
  }
  const handleDelete = async (exo) => {
    if (!confirm(`Supprimer définitivement "${exo.name}" ?`)) return
    try {
      await doDelete(exo.id)
      refetch()
    } catch (e) {
      alert(e?.message || 'Échec de la suppression.')
    }
  }

  return (
    <div>
      {/* Header tab */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap', marginBottom: 14,
      }}>
        <p style={{
          fontFamily: T.mono, fontSize: 11, color: T.muted2,
          margin: 0, lineHeight: 1.5, maxWidth: 600,
        }}>
          Tes procédés personnels, ceux de ton club et la bibliothèque
          Insightball. Chaque procédé est une brique unitaire (Échauffement,
          Jeu, Situation, Exercice ou Match) que tu peux assembler dans une
          séance.
        </p>
        <button
          type="button"
          onClick={() => navigate('/dashboard/seances/insightlab/new')}
          style={btnPrimary()}
        >
          <Plus size={12} /> Nouveau procédé
        </button>
      </div>

      {/* Filtres sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: T.bg, paddingBottom: 12, marginBottom: 16,
        borderBottom: `1px solid ${T.rule}`,
      }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <ToggleBtn
            active={view === 'all'} onClick={() => setView('all')}
            icon={<BookOpen size={12} />}
          >
            Mes procédés
          </ToggleBtn>
          <ToggleBtn
            active={view === 'templates'} onClick={() => setView('templates')}
            icon={<Sparkles size={12} />}
          >
            Templates Insightball
          </ToggleBtn>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {PROCEDES.map(p => (
              <button
                key={p.value || 'all'}
                type="button"
                onClick={() => setProcede(p.value)}
                style={{
                  background: procede === p.value ? T.dark : T.surface,
                  color: procede === p.value ? T.gold : T.ink,
                  border: `2px solid ${T.dark}`,
                  padding: '6px 12px', cursor: 'pointer',
                  fontFamily: T.mono, fontSize: 10, fontWeight: 700,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div style={{
            flex: 1, minWidth: 200,
            display: 'flex', alignItems: 'center', gap: 6,
            border: `2px solid ${T.dark}`, padding: '6px 10px',
            background: T.surface,
          }}>
            <Search size={14} color={T.muted2} />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher (nom, description)..."
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent',
                fontFamily: T.mono, fontSize: 12, color: T.ink,
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {THEMES.map(t => (
            <button
              key={t.value || 'all-themes'}
              type="button"
              onClick={() => setTheme(t.value)}
              style={{
                background: theme === t.value ? T.gold : T.surface,
                color: theme === t.value ? T.dark : T.muted2,
                border: `1px solid ${theme === t.value ? T.gold : T.rule}`,
                padding: '4px 10px', cursor: 'pointer',
                fontFamily: T.mono, fontSize: 9, fontWeight: 700,
                letterSpacing: '.1em', textTransform: 'uppercase',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {loading && <SkeletonGrid />}
      {!loading && error && <ErrorState message={error} onRetry={refetch} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          title="Aucun procédé trouvé"
          message="Modifie tes filtres ou crée un nouveau procédé pour démarrer ta bibliothèque."
          ctaLabel="Créer un procédé"
          onCta={() => navigate('/dashboard/seances/insightlab/new')}
        />
      )}
      {!loading && !error && items.length > 0 && (
        <>
          <div style={{
            fontFamily: T.mono, fontSize: 11, color: T.muted2,
            letterSpacing: '.04em', marginBottom: 12,
          }}>
            {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} sur {total}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}>
            {items.map(exo => (
              <ProcedeCard
                key={exo.id}
                data={exo}
                context="insightlab"
                currentUserId={user?.id}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}
    </div>
  )
}


function ToggleBtn({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? T.gold : T.surface,
        color: active ? T.dark : T.ink,
        border: `2px solid ${T.dark}`,
        padding: '8px 14px', cursor: 'pointer',
        fontFamily: T.mono, fontSize: 10, fontWeight: 700,
        letterSpacing: '.12em', textTransform: 'uppercase',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {icon} {children}
    </button>
  )
}


function Pagination({ page, totalPages, onChange }) {
  const pages = []
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (page > 3) pages.push('...')
    const from = Math.max(2, page - 1)
    const to = Math.min(totalPages - 1, page + 1)
    for (let i = from; i <= to; i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
  }
  const Btn = ({ children, disabled, active, onClick, ariaLabel }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        background: active ? T.dark : T.surface,
        color: active ? T.gold : T.ink,
        border: `2px solid ${T.dark}`,
        padding: '6px 10px', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: T.mono, fontSize: 11, fontWeight: 700,
        letterSpacing: '.06em', minWidth: 32,
      }}
    >
      {children}
    </button>
  )
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      gap: 6, marginTop: 28,
    }}>
      <Btn disabled={page === 1} onClick={() => onChange(page - 1)} ariaLabel="Page précédente">
        <ChevronLeft size={14} />
      </Btn>
      {pages.map((p, i) => p === '...' ? (
        <span key={`e${i}`} style={{ fontFamily: T.mono, color: T.muted, padding: '0 4px' }}>…</span>
      ) : (
        <Btn key={p} active={p === page} onClick={() => onChange(p)}>{p}</Btn>
      ))}
      <Btn disabled={page === totalPages} onClick={() => onChange(page + 1)} ariaLabel="Page suivante">
        <ChevronRight size={14} />
      </Btn>
    </div>
  )
}
